import React, { useEffect, useMemo, useState } from "react";
import { Button, Row, Col, Input, Icon, message, Divider, Popconfirm, Badge } from "antd";

import { QATable } from "../../components/qa_custom/index";
import { getMenus, getMenusNotInactive, toggleMenuStatus } from "./actions/index";
import { MenuEditor } from "./components/menuEditor";
import { downloadFile } from "../../components/util";
import { ClearFilterButton } from "../../components/ListButtons/clearFilter";
import { ExportButtons } from "../../components/ListButtons/exportTo";
import { ColumnProps, SorterResult } from "antd/lib/table";
import { IMeta, ITableFilter, IMenuList, KeyObject, ITableSort } from "./types";
import { Session } from "../../business";

export const MenuSettings = () => {
    const [loadingData, setLoadingData] = useState(true);
    const [menuList, setMenuList] = useState<IMenuList[]>([]);
    const [tableFilter, setTableFilter] = useState<ITableFilter>({
        filter: {},
        pagination: {
            page: 1,
            perPage: 10,
        },
    });

    const [meta, setMeta] = useState<IMeta>({ page: 1, perPage: 10 });

    const [detailMenu, setDetailMenu] = useState<IMenuList | null>(null);
    const [reloadList, setReloadList] = useState(false);

    const [menusNotInative, setMenusNotInative] = useState<string[]>([]);

    const { filter } = tableFilter;

    const isQa = Session.getUsername() === "qa";

    useEffect(() => {
        setLoadingData(true);

        const { filter, pagination, sorter } = tableFilter;

        getMenus(pagination, filter, sorter)
            .then((result) => {
                if (!result) {
                    message.error("Ocorreu um erro");
                    return;
                }

                setMenuList(result.list);

                const { metadata } = result;
                setMeta({
                    page: Number(metadata.pagina),
                    perPage: Number(metadata.resultados_pagina),
                    total: Number(metadata.resultados),
                });
            })
            .finally(() => {
                setLoadingData(false);
            });
    }, [tableFilter, reloadList]);

    useEffect(() => {
        if (isQa) {
            getMenusNotInactive().then((result) => {
                if (result) {
                    setMenusNotInative(result);
                }
            });
        }
    }, [isQa]);

    const exportList = async (type: "pdf" | "xls") => {
        const REPORT_PARAMS = {
            accao: "lista",
            campos: "titulo:24:Título;descricao:24:Descrição;pai:24:Menu pai",
            titulo: "Menus",
        };

        const { filter, sorter } = tableFilter;

        const filterOrderExport: KeyObject = {};

        Object.entries(filter).forEach(([key, value]) => {
            filterOrderExport[`filtro_${key}`] = value;
        });

        if (sorter && sorter.sorterBy && sorter.sorterOrder) {
            filterOrderExport[`ordenacao_${sorter.sorterBy}`] = sorter.sorterOrder === "descend" ? "DESC" : "ASC";
        }

        await downloadFile("geremenus.php", {
            ...REPORT_PARAMS,
            ...filterOrderExport,
            saida: type,
            downloadInBrowser: true,
        });
    };

    //---------------------------- filter --------------------------------
    const handleSearch = (_: React.Key[], confirm: () => void) => {
        confirm();
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
    };

    function getColumnSearchProps(key: string) {
        let searchInput: Input | null = null;

        const filteredValue = filter[key];
        return {
            filterDropdown: ({
                setSelectedKeys,
                selectedKeys,
                confirm,
                clearFilters,
            }: {
                setSelectedKeys: (selectedKeys: string[]) => void;
                selectedKeys: React.Key[];
                confirm: () => void;
                clearFilters: () => void;
            }) => (
                <div style={{ padding: 8 }}>
                    <Input
                        ref={(node) => {
                            searchInput = node;
                        }}
                        placeholder={`Pesquisar`}
                        value={selectedKeys[0]}
                        onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                        onPressEnter={() => handleSearch(selectedKeys, confirm)}
                        style={{
                            width: 188,
                            marginBottom: 8,
                            display: "block",
                        }}
                    />
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys, confirm)}
                        icon="search"
                        size="small"
                        style={{ width: 90, marginRight: 8 }}
                    >
                        Pesquisar
                    </Button>
                    <Button onClick={() => handleReset(clearFilters)} size="small" style={{ width: 90 }}>
                        limpar
                    </Button>
                </div>
            ),
            filterIcon: (filtered: boolean) => (
                <Icon type="search" style={{ color: filtered ? "#1890ff" : undefined }} />
            ),
            filteredValue: filteredValue ? [filteredValue] : [],
            onFilterDropdownVisibleChange: (visibleDropdown: boolean) => {
                if (visibleDropdown) {
                    setTimeout(() => searchInput?.focus(), 100);
                }
            },
        };
    }

    function getColumnSortProps(dataIndex: string) {
        const { sorter } = tableFilter;
        const columnSort = sorter && sorter.sorterBy === dataIndex ? sorter.sorterOrder : null;
        return {
            sorter: true,
            sortOrder: columnSort ?? undefined,
        };
    }

    function handleTableChange(
        _pagination: KeyObject,
        filters: Partial<Record<keyof IMenuList, string[]>>,
        sorter: SorterResult<IMenuList>
    ) {
        const objFilter: KeyObject = {};

        if (filters) {
            Object.entries(filters).forEach(([key, val]) => {
                if (val && val[0]) objFilter[key] = val[0];
            });
        }

        let sortObj: ITableSort | null = null;
        if (sorter.columnKey && sorter.order) {
            sortObj = {
                sorterBy: sorter.columnKey,
                sorterOrder: sorter.order,
            };
        }

        setTableFilter((state) => ({
            ...state,
            pagination: {
                ...state.pagination,
                page: 1,
            },
            filter: objFilter,
            sorter: sortObj ?? undefined,
        }));
    }

    function onPageChange(page: number, perPage: number | undefined) {
        setTableFilter((state) => ({
            ...state,
            pagination: {
                page,
                perPage: perPage ?? state.pagination.perPage,
            },
        }));
    }

    //---------------------------- endfilter -----------------------------

    function handleShowMenu(menu: IMenuList) {
        setDetailMenu(menu);
    }

    function onCloseEditor(reload?: boolean) {
        if (reload) {
            setReloadList((state) => !state);
        }
        setDetailMenu(null);
    }

    function clearFilters() {
        setTableFilter((state) => ({
            pagination: { ...state.pagination },
            filter: {},
        }));
    }

    function handleToggleMenuStatus(id: string, active: boolean) {
        toggleMenuStatus(id, active).then((result) => {
            if (result?.sucesso) {
                message.success(active ? "Menu ativado" : "Menu inativado");
                setReloadList((state) => !state);
            } else {
                message.error(result.erro ?? "Ocorreu um erro");
            }
        });
    }

    const columns: ColumnProps<IMenuList>[] = [
        {
            title: <span style={{ marginLeft: 8 }}>Título</span>,
            dataIndex: "titulo",
            key: "titulo",
            ...getColumnSearchProps("titulo"),
            ...getColumnSortProps("titulo"),
            ellipsis: true,
            render: (text, record) => (
                <div style={{ marginLeft: 8 }}>
                    <Badge color={record.activo === "t" ? "green" : "#f50"} />
                    <span title={record.activo === "t" ? "Ativo" : "Inativo"}>{text}</span>
                </div>
            ),
        },
        {
            title: "Descrição",
            dataIndex: "descricao",
            key: "descricao",
            ...getColumnSearchProps("descricao"),
            ...getColumnSortProps("descricao"),
            ellipsis: true,
        },
        {
            title: "Menu Pai",
            dataIndex: "pai",
            key: "pai",
            ...getColumnSearchProps("pai"),
            ...getColumnSortProps("pai"),
            ellipsis: true,
        },
        {
            title: isQa ? "Ações" : "Ação",
            render: (_, record) => {
                const isToShowInactiveAction = isQa && !menusNotInative.some((f) => f === record.idmenu);
                return (
                    <>
                        <Button
                            type="link"
                            style={{
                                padding: 0,
                            }}
                            onClick={() => handleShowMenu(record)}
                        >
                            Consultar
                        </Button>

                        {isToShowInactiveAction && (
                            <>
                                <Divider type="vertical" />
                                {record.activo === "t" ? (
                                    <Popconfirm
                                        title="Tem certeza que pretende inativar?"
                                        onConfirm={() => handleToggleMenuStatus(record.idmenu, false)}
                                        okText="Sim"
                                        cancelText="Não"
                                    >
                                        <Button type="link" style={{ padding: 0, color: "red" }}>
                                            Inativar
                                        </Button>
                                    </Popconfirm>
                                ) : (
                                    <Popconfirm
                                        title="Tem certeza que pretende ativar?"
                                        onConfirm={() => handleToggleMenuStatus(record.idmenu, true)}
                                        okText="Sim"
                                        cancelText="Não"
                                    >
                                        <Button type="link" style={{ padding: 0 }}>
                                            Ativar
                                        </Button>
                                    </Popconfirm>
                                )}
                            </>
                        )}
                    </>
                );
            },
        },
    ];

    const showClearFilterButton = useMemo(() => {
        if (Object.keys(filter).length !== 0) {
            return true;
        }

        return false;
    }, [filter]);

    return (
        <>
            <h3>Listagem de Menus</h3>
            <Row type="flex" justify="end" align="top">
                {showClearFilterButton && <ClearFilterButton onClick={clearFilters} />}
                <ExportButtons onExport={exportList} />

                <Col span={24}>
                    <QATable
                        columns={columns}
                        data={menuList}
                        loadingData={loadingData}
                        datakey={"idmenu"}
                        currentPage={meta.page}
                        totalResult={meta.total}
                        pageChange={onPageChange}
                        tableChange={handleTableChange}
                        showRowSelection={false}
                    />
                </Col>
            </Row>

            {detailMenu && <MenuEditor onCloseEditor={onCloseEditor} modelForm={detailMenu} />}
        </>
    );
};
